<template>
  <v-navigation-drawer class="elevation-14" app permanent :mini-variant="isMini" :mini-variant-width="72">
    <!-- 사이드바 최상단 유틸리티 바 (로그아웃 · 테마 토글 · 접기/펼치기) -->
    <utility-bar :is-mini="isMini" @toggle-mini="isMini = !isMini" />

    <!-- 메뉴 위 섹션: 재화 칩 + 아바타 + 이름 + 상태 패널 -->
    <sidebar-header :is-mini="isMini" />

    <v-divider></v-divider>
    <v-list dense shaped>
      <div v-for="item in displayItems" :key="item.title + item.to">
        <!-- 외부 링크 설정 -->
        <v-list-item link v-if="item.to.startsWith('https://')">
          <a :href="item.to" target="_blank" style="display: flex; text-decoration: none; color: inherit">
            <div :class="{ 'ml-2': item.indent }">
              <v-list-item-icon v-if="item.icon.includes('mdi')">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-icon v-else>
                <!-- <v-chzzk-icon :size="24" contain></v-chzzk-icon> -->
                <v-broadcast-icon :size="24" contain></v-broadcast-icon>
              </v-list-item-icon>
            </div>

            <v-list-item-content>
              <v-list-item-title
                >{{ item.title }}

                <v-icon class="ml-1" v-if="item.to.startsWith('https://')" small>mdi-open-in-new</v-icon>
              </v-list-item-title>
            </v-list-item-content>
          </a>
        </v-list-item>
        <!-- exact: /my(허브)가 /my/broadcast의 접두사라 둘 다 active되는 것 방지 -->
        <v-list-item v-else exact :class="{ 'prevent-clickable': item.to === '' }" link :to="item.to">
          <div :class="{ 'ml-4': item.indent }" style="width: 40px; height: 40px">
            <v-list-item-icon v-if="item.icon.includes('mdi')">
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-icon v-else>
              <div class="d-flex align-center">
                <v-chzzk-icon v-if="item.icon === 'chzzk'" style="border-radius: 4px" :size="22" contain></v-chzzk-icon>
                <v-custom-icon v-else :name="item.icon" :size="24" contain></v-custom-icon>
              </div>
            </v-list-item-icon>
          </div>

          <v-list-item-content>
            <v-list-item-title class="the-left-bar__title-font" style="font-size: 18px"
              >{{ item.title }}

              <v-icon class="ml-1" v-if="item.to.startsWith('https://')" small>mdi-open-in-new</v-icon>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
    </v-list>

    <!-- 로컬 전용: 팬 공간으로 바로 전환 (개발 편의) -->
    <template v-if="isLocalEnv" #append>
      <div class="pa-3">
        <v-btn text block small color="primary" @click="goFan">
          <v-icon left small>mdi-swap-horizontal</v-icon>팬으로 전환 (local)
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import VChzzkIcon from "../../components/icons/VChzzkIcon.vue";
import { isLocal } from "@/utils/env-utils";
import { NAV_CATEGORIES } from "@/constants/navMenu";
import SidebarHeader from "./c/SidebarHeader.vue";
import UtilityBar from "./c/UtilityBar.vue";

export default {
  components: { VChzzkIcon, SidebarHeader, UtilityBar },
  data() {
    return {
      html: null,
      primaryColor: "#d000ff82",
      isFailedImage: false,
      // 사이드바 접힘 상태 (localStorage 유지)
      isMini: localStorage.getItem("sidebar/mini") === "true",
    };
  },
  watch: {
    isMini(value) {
      localStorage.setItem("sidebar/mini", value ? "true" : "false");
    },
  },
  computed: {
    // 메뉴 — 공용 NAV_CATEGORIES(중첩 구조) 소비. titleKey→$t 해석 + localOnly 필터.
    //   (편집은 src/constants/navMenu.js 에서. 허브 페이지와 단일 소스 공유)
    categories() {
      const resolveTitle = (n) => (n.titleKey ? this.$t(n.titleKey) : n.title);
      const visible = (n) => !n.localOnly || isLocal();
      // hideInSidebar: 사이드바에서만 숨김(허브 페이지는 NAV_CATEGORIES 직접 소비라 영향 없음)
      return NAV_CATEGORIES.filter((cat) => visible(cat) && !cat.hideInSidebar).map((cat) => ({
        ...cat,
        title: resolveTitle(cat),
        subCategories: cat.subCategories
          ? cat.subCategories.filter(visible).map((sub) => ({ ...sub, title: resolveTitle(sub) }))
          : undefined,
      }));
    },
    // 렌더용 평면화: 카테고리 → 헤더(to=cat.to, 허브 있으면 클릭가능) + 서브(indent). 템플릿은 평면 리스트만 안다.
    items() {
      return this.categories.flatMap((cat) => {
        if (!cat.subCategories) return [cat]; // 리프
        // hubOnly: 사이드 메뉴엔 서브 숨기고 헤더(허브 링크) 한 항목만 노출
        if (cat.hubOnly) return [{ title: cat.title, icon: cat.icon, to: cat.to || "" }];
        return [
          { title: cat.title, icon: cat.icon, to: cat.to || "" },
          // sub.hubOnly: 사이드바엔 숨김(허브 페이지에서만 노출)
          ...cat.subCategories.filter((sub) => !sub.hubOnly).map((sub) => ({ ...sub, indent: true })),
        ];
      });
    },
    // 접었을 때(mini)는 서브(indent) 제외하고 큰 카테고리만 노출
    displayItems() {
      return this.isMini ? this.items.filter((i) => !i.indent) : this.items;
    },
    isLocalEnv() {
      return isLocal();
    },
  },
  methods: {
    // 로컬 전용: 팬 공간으로 전환 — 리로드 없이 store 즉시 갱신(사이드바 반응) + 라우팅
    goFan() {
      localStorage.setItem("auth/role", "fan");
      this.$store.commit("__setIsFan", true);
      this.$router.push("/fan").catch(() => {});
    },
  },
};
</script>

<style scoped>
>>> .v-navigation-drawer__content,
>>> .v-navigation-drawer__append {
  /* 사이드바 배경 — 테마-가변 토큰(라이트=연한 회백 / 다크=깊은 다크) */
  background-color: var(--v-sidebarBg-base);
}

/* 비활성 메뉴 — 차분한 중립 텍스트(테마-가변 토큰) */
>>> .v-list-item:not(.v-list-item--active) .v-list-item__title {
  color: var(--v-sidebarText-base);
}
/* 활성 메뉴 — 세련된 액센트(텍스트 + Vuetify 오버레이가 currentColor라 배경도 은은히 틴트) */
>>> .v-list-item--active .v-list-item__title {
  color: var(--v-sidebarAccent-base);
}
>>> .v-list-item--active {
  color: var(--v-sidebarAccent-base);
}

.append-banner {
  background-color: white;
  margin: 12px;
  padding: 8px;
  border-radius: 4px;
}

.prevent-clickable {
  pointer-events: none;
}
</style>
